import React, { useState, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Upload, Camera, X } from "lucide-react";

const categorias = [
  { value: "veiculo", label: "Veículo", icon: "🚗" },
  { value: "maquina_agricola", label: "Máquina Agrícola", icon: "🚜" },
  { value: "implemento", label: "Implemento", icon: "🔧" },
  { value: "equipamento", label: "Equipamento", icon: "⚙️" },
  { value: "edificacao", label: "Edificação", icon: "🏠" },
  { value: "terreno", label: "Terreno", icon: "🌾" },
  { value: "mobiliario", label: "Mobiliário", icon: "🪑" },
  { value: "informatica", label: "Informática", icon: "💻" },
  { value: "outro", label: "Outro", icon: "📦" }
];

const vidaUtilPadrao = {
  veiculo: 60,
  maquina_agricola: 120,
  implemento: 120,
  equipamento: 120,
  edificacao: 300,
  terreno: 0,
  mobiliario: 120,
  informatica: 60,
  outro: 120
};

export default function AtivoForm({ ativo, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  const [formData, setFormData] = useState({
    nome: "",
    categoria: "equipamento",
    numero_serie: "",
    marca: "",
    modelo: "",
    ano_fabricacao: "",
    data_aquisicao: "",
    valor_aquisicao: "",
    valor_residual: "",
    vida_util_meses: 120,
    metodo_depreciacao: "linear",
    localizacao: "",
    responsavel: "",
    status: "ativo",
    placa: "",
    renavam: "",
    chassi: "",
    nota_fiscal: "",
    fornecedor: "",
    garantia_ate: "",
    observacoes: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ativo) {
      setFormData({
        nome: ativo.nome || "",
        categoria: ativo.categoria || "equipamento",
        numero_serie: ativo.numero_serie || "",
        marca: ativo.marca || "",
        modelo: ativo.modelo || "",
        ano_fabricacao: ativo.ano_fabricacao?.toString() || "",
        data_aquisicao: ativo.data_aquisicao || "",
        valor_aquisicao: ativo.valor_aquisicao?.toString() || "",
        valor_residual: ativo.valor_residual?.toString() || "",
        vida_util_meses: ativo.vida_util_meses || 120,
        metodo_depreciacao: ativo.metodo_depreciacao || "linear",
        localizacao: ativo.localizacao || "",
        responsavel: ativo.responsavel || "",
        status: ativo.status || "ativo",
        placa: ativo.placa || "",
        renavam: ativo.renavam || "",
        chassi: ativo.chassi || "",
        nota_fiscal: ativo.nota_fiscal || "",
        fornecedor: ativo.fornecedor || "",
        garantia_ate: ativo.garantia_ate || "",
        foto_url: ativo.foto_url || "",
        observacoes: ativo.observacoes || ""
      });
    }
  }, [ativo]);

  const handleCategoriaChange = (value) => {
    setFormData({
      ...formData,
      categoria: value,
      vida_util_meses: vidaUtilPadrao[value] || 120
    });
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
    setFormData({ ...formData, foto_url: file_url });
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      data_aquisicao: formData.data_aquisicao ? formData.data_aquisicao : null,
      garantia_ate: formData.garantia_ate ? formData.garantia_ate : null,
      propriedade_id: propriedadeId,
      ano_fabricacao: formData.ano_fabricacao ? parseInt(formData.ano_fabricacao) : null,
      valor_aquisicao: parseFloat(formData.valor_aquisicao) || 0,
      valor_residual: formData.valor_residual ? parseFloat(formData.valor_residual) : 0,
      vida_util_meses: parseInt(formData.vida_util_meses) || 120
    };

    if (ativo) {
      await agrogestao.entities.AtivoImobilizado.update(ativo.id, data);
    } else {
      await agrogestao.entities.AtivoImobilizado.create(data);
    }

    setSaving(false);
    onSuccess();
  };

  const isVeiculo = formData.categoria === 'veiculo';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Categoria e Nome */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select value={formData.categoria} onValueChange={handleCategoriaChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categorias.map(c => (
                <SelectItem key={c.value} value={c.value}>
                  <span className="flex items-center gap-2">
                    <span>{c.icon}</span> {c.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Nome/Descrição *</Label>
          <Input
            placeholder="Ex: Trator John Deere 6110J"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Identificação */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Marca</Label>
          <Input
            placeholder="Fabricante"
            value={formData.marca}
            onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Modelo</Label>
          <Input
            placeholder="Modelo"
            value={formData.modelo}
            onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Nº Série</Label>
          <Input
            placeholder="Número de série"
            value={formData.numero_serie}
            onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
          />
        </div>
      </div>

      {/* Dados de Veículo */}
      {isVeiculo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-xl">
          <div className="space-y-2">
            <Label>Placa</Label>
            <Input
              placeholder="ABC-1234"
              value={formData.placa}
              onChange={(e) => setFormData({ ...formData, placa: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>RENAVAM</Label>
            <Input
              value={formData.renavam}
              onChange={(e) => setFormData({ ...formData, renavam: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Chassi</Label>
            <Input
              value={formData.chassi}
              onChange={(e) => setFormData({ ...formData, chassi: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Aquisição */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Ano Fabricação</Label>
          <Input
            type="number"
            placeholder="2020"
            value={formData.ano_fabricacao}
            onChange={(e) => setFormData({ ...formData, ano_fabricacao: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Data Aquisição *</Label>
          <Input
            type="date"
            value={formData.data_aquisicao}
            onChange={(e) => setFormData({ ...formData, data_aquisicao: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Valor Aquisição *</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.valor_aquisicao}
            onChange={(e) => setFormData({ ...formData, valor_aquisicao: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Fornecedor</Label>
          <Input
            placeholder="Vendedor"
            value={formData.fornecedor}
            onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })}
          />
        </div>
      </div>

      {/* Depreciação */}
      <div className="p-4 bg-amber-50 rounded-xl space-y-4">
        <h4 className="font-semibold text-amber-800">Depreciação</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Método</Label>
            <Select value={formData.metodo_depreciacao} onValueChange={(v) => setFormData({ ...formData, metodo_depreciacao: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="linear">Linear</SelectItem>
                <SelectItem value="soma_digitos">Soma dos Dígitos</SelectItem>
                <SelectItem value="horas_uso">Horas de Uso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Vida Útil (meses)</Label>
            <Input
              type="number"
              value={formData.vida_util_meses}
              onChange={(e) => setFormData({ ...formData, vida_util_meses: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Valor Residual</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="0,00"
              value={formData.valor_residual}
              onChange={(e) => setFormData({ ...formData, valor_residual: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Localização e Responsável */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Localização</Label>
          <Input
            placeholder="Onde está o ativo"
            value={formData.localizacao}
            onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Responsável</Label>
          <Input
            placeholder="Pessoa responsável"
            value={formData.responsavel}
            onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="em_manutencao">Em Manutenção</SelectItem>
              <SelectItem value="inativo">Inativo</SelectItem>
              <SelectItem value="vendido">Vendido</SelectItem>
              <SelectItem value="descartado">Descartado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Garantia e NF */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nota Fiscal</Label>
          <Input
            placeholder="Número da NF"
            value={formData.nota_fiscal}
            onChange={(e) => setFormData({ ...formData, nota_fiscal: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Garantia até</Label>
          <Input
            type="date"
            value={formData.garantia_ate}
            onChange={(e) => setFormData({ ...formData, garantia_ate: e.target.value })}
          />
        </div>
      </div>

      {/* Foto do Ativo */}
      <div className="space-y-2">
        <Label>Foto do Ativo</Label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFotoUpload}
          className="hidden"
          id="foto-upload"
        />
        {formData.foto_url ? (
          <div className="relative group rounded-xl overflow-hidden border border-stone-200">
            <img
              src={formData.foto_url}
              alt="Foto do ativo"
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
              <label
                htmlFor="foto-upload"
                className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white text-stone-800 rounded-lg cursor-pointer transition-colors text-sm font-medium"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Trocar
              </label>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, foto_url: "" })}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <X className="w-4 h-4" />
                Remover
              </button>
            </div>
          </div>
        ) : (
          <label
            htmlFor="foto-upload"
            className="flex flex-col items-center justify-center gap-2 w-full h-36 border-2 border-dashed border-stone-300 hover:border-[#40916C] rounded-xl cursor-pointer transition-colors bg-stone-50 hover:bg-green-50"
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
            ) : (
              <>
                <Camera className="w-8 h-8 text-stone-400" />
                <span className="text-sm text-stone-500">Clique para enviar uma foto</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Informações adicionais..."
          value={formData.observacoes}
          onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />{ativo ? "Atualizar" : "Cadastrar"}</>}
        </Button>
      </div>
    </form>
  );
}

export { categorias };
